import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassBottom from '@mui/icons-material/HourglassBottom';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import PendingDocumentsDialog from '../dialogs/PendingDocumentsDialog';
import TooltipTypography from '../typography/TooltipTypography';
import { useIsMobile } from '../../hooks';
import useRatificationRestrictions from '../../hooks/useRatificationRestrictions';
import ONBOARDING_TOOLTIP_CLASS_NAME from '../../theme/constants/tooltip';
import { COLORED_BORDERED_STACK_CLASS_NAME } from '../../theme/constants/stack';
import { DEFAULT_ICON_CLASS_NAME, SECONDARY_ICON_CLASS_NAME } from '../../theme/constants/icon';

const ICON_MAPPING = {
  OFFERED: { icon: <CheckCircleIcon color="success" />, label: 'Aceptada' },
  REJECTED: { icon: <CancelIcon className={SECONDARY_ICON_CLASS_NAME} />, label: 'No financiable' },
  PENDINGDOCUMENTS: {
    icon: <HourglassBottom className={SECONDARY_ICON_CLASS_NAME} />,
    label: 'Requiere documentos',
  },
  DOCUMENTSREVIEWED: {
    icon: <FindInPageIcon className={SECONDARY_ICON_CLASS_NAME} />,
    label: 'Revisión de Documentos',
    tooltipTitle: 'Recibimos tus documentos enviados, te notificaremos cuándo la evaluación se complete.',
    labelColor: 'gray.A400',
  },
  DEFAULT: { icon: <PsychologyIcon className={SECONDARY_ICON_CLASS_NAME} />, label: 'Evaluando', labelColor: 'text.primary' },
};

const PENDING_DOCUMENTS = 'PENDINGDOCUMENTS';

const EvaluationStatusCell = ({ invoice, type, customDialogTitle, customDialogSubtitle }) => {
  const isMobile = useIsMobile('tablet');
  const status = invoice.preoffer?.preofferevaluationrequest?.status;
  const filteredRestrictions = invoice.restrictedBy?.filter(
    (res) => ['CREATED', 'PENDING'].includes(res.status)
      && res.restrictionType === type
      && res.restriction !== 'OTHER',
  );
  const executiveAssigned = invoice.company?.executiveAssigned;

  const [openDialog, setOpenDialog] = useState(false);
  const { icon, label, labelColor, tooltipTitle } = ICON_MAPPING[status] || ICON_MAPPING.DEFAULT;

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);
  const { pendingRestrictions } = useRatificationRestrictions(
    invoice,
    type,
    filteredRestrictions || [],
  );

  return (
    <>
      <Stack direction="row" alignItems="center" onClick={handleOpenDialog} spacing={0.5} sx={{ cursor: filteredRestrictions.length > 0 ? 'pointer' : 'default' }}>
        {!isMobile && (
          <TooltipTypography
            title={tooltipTitle || label}
            tooltipClassName={ONBOARDING_TOOLTIP_CLASS_NAME}
          >
            {icon}
          </TooltipTypography>
        )}
        <Typography sx={{ maxWidth: { xs: 85, md: 100 }, color: labelColor }}>{label}</Typography>
        {status === PENDING_DOCUMENTS && (
          <Stack
            className={COLORED_BORDERED_STACK_CLASS_NAME}
            sx={{
              borderRadius: '50%',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? 16 : 20,
              height: isMobile ? 16 : 20,
              cursor: 'pointer',
              ml: isMobile ? 0 : 1,
            }}
            onClick={handleOpenDialog}
          >
            <ChevronRightOutlinedIcon
              className={
              DEFAULT_ICON_CLASS_NAME
}
              sx={{ fontSize: { xs: 16, md: 20 } }}
            />
          </Stack>
        )}
      </Stack>
      {status === PENDING_DOCUMENTS && openDialog && (
        <PendingDocumentsDialog
          invoiceId={invoice.id}
          open={openDialog}
          onClose={handleCloseDialog}
          type={type}
          filteredRestrictions={pendingRestrictions}
          executiveAssigned={executiveAssigned}
          customDialogTitle={customDialogTitle}
          customDialogSubtitle={customDialogSubtitle}
        />
      )}
    </>
  );
};

EvaluationStatusCell.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.string,
    offer: PropTypes.shape({
      transferCondition: PropTypes.string,
    }),
    reviewDocumentsManager: PropTypes.shape({}),
    preoffer: PropTypes.shape({
      preofferevaluationrequest: PropTypes.shape({
        status: PropTypes.string,
      }),
    }),
    restrictedBy: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
        restrictionType: PropTypes.string,
        restriction: PropTypes.string,
        id: PropTypes.string,
        restrictionLabel: PropTypes.string,
        description: PropTypes.string,
        statusLabel: PropTypes.string,
      }),
    ),
    company: PropTypes.shape({
      executiveAssigned: PropTypes.shape({
        id: PropTypes.string,
        completeName: PropTypes.string,
        email: PropTypes.string,
        phoneNumber: PropTypes.string,
      }),
    }),
  }).isRequired,
  type: PropTypes.oneOf(['Risk', 'Operations']),
  customDialogTitle: PropTypes.string,
  customDialogSubtitle: PropTypes.string,
};

EvaluationStatusCell.defaultProps = {
  type: 'Risk',
  customDialogTitle: null,
  customDialogSubtitle: null,
};

export default EvaluationStatusCell;
